<script setup lang="ts">
import { ListChecks } from "@lucide/vue";
import { computed } from "vue";
import { liveConnected } from "@/api/events";
import { isRunning, useJobs } from "@/api/jobs";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import JobItem from "@/components/items/JobItem.vue";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";

definePage({ meta: { title: "Tasks & Jobs" } });

const { can } = useSession();
const isAdmin = computed(() => can("admin.system"));
const status = useRouteQuery("status");
const everyone = useRouteQuery("all");
const q = useRouteQuery("q");

const jobs = useJobs(() => ({ all: isAdmin.value && everyone.value === "true", status: status.value || undefined }));
const list = computed(() => {
  const needle = q.value.trim().toLowerCase();
  return (jobs.data.value ?? []).filter((j) => !needle || j.name.toLowerCase().includes(needle) || j.kind.toLowerCase().includes(needle));
});
const anyRunning = computed(() => (jobs.data.value ?? []).some(isRunning));

const statusOptions = [
  { value: "working", label: "Running" },
  { value: "queued", label: "Queued" },
  { value: "input_required", label: "Waiting" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];
const statusModel = computed({ get: () => status.value || undefined, set: (v: string | string[] | undefined) => (status.value = (v as string) ?? "") });
</script>

<template>
  <div>
    <PageHeader :title="t.jobs.title" :description="t.jobs.description">
      <template #badges>
        <span v-if="anyRunning || liveConnected" class="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span class="relative flex size-2"><span class="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" /><span class="relative inline-flex size-2 rounded-full bg-success" /></span>
          {{ t.jobs.live }}
        </span>
      </template>
    </PageHeader>

    <FilterBar v-model:search="q" placeholder="Filter by name…" label="Filter jobs" class="mb-5">
      <template v-if="isAdmin" #end>
        <div class="flex items-center gap-2">
          <Switch id="all-jobs" :model-value="everyone === 'true'" @update:model-value="(v: boolean) => (everyone = v ? 'true' : '')" />
          <Label for="all-jobs" class="font-normal">{{ t.jobs.everyone }}</Label>
        </div>
      </template>
      <template #chips><FilterChips v-model="statusModel" :options="statusOptions" label="Status" /></template>
    </FilterBar>

    <ListSkeleton v-if="jobs.isPending.value" :rows="5" />
    <ErrorState v-else-if="jobs.error.value" :error="jobs.error.value" @retry="jobs.refetch()" />
    <EmptyState v-else-if="list.length === 0" :icon="ListChecks" :title="status || q ? t.common.noResults : t.jobs.empty" :description="t.jobs.emptyHint" />
    <div v-else class="flex flex-col gap-2">
      <JobItem v-for="job in list" :key="job.id" :job="job" />
    </div>
  </div>
</template>
